import { useNostr } from './useNostr';
import { useCurrentUser } from './useCurrentUser';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useToast } from './useToast';

/**
 * Hook to follow multiple users at once (bulk follow)
 * Publishes a single kind 3 event with all new p tags added
 */
export function useBulkFollow() {
  const { nostr } = useNostr();
  const { user } = useCurrentUser();
  const { toast } = useToast();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (pubkeys: string[]) => {
      if (!nostr || !user) throw new Error('Must be logged in to follow');
      if (pubkeys.length === 0) return { followed: 0 };

      // Get current follow list
      const events = await nostr.query([{ kinds: [3], authors: [user.pubkey], limit: 1 }]);
      const currentTags = events[0]?.tags ?? [];
      const existingFollows = new Set(
        currentTags.filter(([name]) => name === 'p').map(([_, pk]) => pk)
      );

      // Only add pubkeys not already followed
      const alreadyFollowing: string[] = [];
      const newPubkeys: string[] = [];

      for (const pk of pubkeys) {
        if (existingFollows.has(pk)) {
          alreadyFollowing.push(pk);
        } else {
          newPubkeys.push(pk);
        }
      }

      if (newPubkeys.length === 0) {
        return { followed: 0, already: alreadyFollowing.length };
      }

      // Add new p tags
      const newTags = [
        ...currentTags,
        ...newPubkeys.map(pk => ['p', pk]),
      ];

      // Publish updated kind 3 contact list
      const event = await user.signer.signEvent({
        kind: 3,
        content: '',
        tags: newTags,
        created_at: Math.floor(Date.now() / 1000),
      });

      await nostr.event(event, { signal: AbortSignal.timeout(5000) });

      return { followed: newPubkeys.length, already: alreadyFollowing.length };
    },
    onSuccess: (result) => {
      if (result.followed > 0) {
        toast({
          title: 'Followed! ✨',
          description: `You are now following ${result.followed} account${result.followed > 1 ? 's' : ''}${result.already > 0 ? `. ${result.already} already followed.` : '.'}`,
        });
      } else if (result.already > 0) {
        toast({
          title: 'Already Following',
          description: `You were already following all ${result.already} account${result.already > 1 ? 's' : ''} in this pack.`,
        });
      }

      // Invalidate relevant queries
      queryClient.invalidateQueries({ queryKey: ['following-count'] });
      queryClient.invalidateQueries({ queryKey: ['my-contacts'] });
      queryClient.invalidateQueries({ queryKey: ['is-following'] });
      queryClient.invalidateQueries({ queryKey: ['following-feed'] });
    },
    onError: (error) => {
      toast({
        title: 'Follow failed',
        description: error.message,
        variant: 'destructive',
      });
    },
  });
}